/**
 * NOTES-V2 N4 — agrupamento temporal/por-tipo + algoritmo de hero.
 *
 * Não muda payload. Recebe lista flat de notes (mesma shape do
 * GET /api/notes) e devolve buckets + escolha de hero note.
 *
 * Modos:
 *   "date" (default): Hoje / Esta semana / Este mês / Mais antigas
 *   "type":           Ideias / Diários / Desabafos / Pesquisas /
 *                     Revisões / Gerais
 */

import { Lightbulb, BookOpen, Heart, FileText, Search, Sparkles } from "lucide-react";

// ===================== NOTE TYPES =====================

// NOTES-V2 N3 — research + daily_review entries adicionadas pra cobrir
// tipos que vinham do agent e caíam no fallback "general".
export const NOTE_TYPES = {
  idea: {
    label: "Ideia",
    plural: "Ideias",
    icon: Lightbulb,
    color: "#f59e0b",
    bg: "bg-amber-500/10",
  },
  journal: {
    label: "Diário",
    plural: "Diários",
    icon: BookOpen,
    color: "#a855f7",
    bg: "bg-purple-500/10",
  },
  vent: {
    label: "Desabafo",
    plural: "Desabafos",
    icon: Heart,
    color: "#ec4899",
    bg: "bg-pink-500/10",
  },
  research: {
    label: "Pesquisa",
    plural: "Pesquisas",
    icon: Search,
    color: "#3b82f6",
    bg: "bg-blue-500/10",
  },
  daily_review: {
    label: "Revisão",
    plural: "Revisões",
    icon: Sparkles,
    color: "#c084fc",
    bg: "bg-fuchsia-500/10",
  },
  general: {
    label: "Geral",
    plural: "Gerais",
    icon: FileText,
    color: "#06b6d4",
    bg: "bg-cyan-500/10",
  },
};

export function noteTypeOf(note) {
  if (!note) return NOTE_TYPES.general;
  return NOTE_TYPES[note.type] || NOTE_TYPES.general;
}

// ===================== DATE GROUPING =====================
// PRODUTO-V1 N6 — "pinned" é grupo virtual no TOPO. Pinned saem
// dos grupos temporais pra não duplicar.

export const DATE_GROUP_ORDER = ["pinned", "today", "this_week", "this_month", "older"];

export const DATE_GROUP_LABELS = {
  pinned: "Fixadas",
  today: "Hoje",
  this_week: "Esta semana",
  this_month: "Este mês",
  older: "Mais antigas",
};

export const DATE_GROUP_COLORS = {
  pinned: { dot: "bg-amber-400", text: "text-amber-300", count: "text-amber-400" },
  today: { dot: "bg-cyan-500", text: "text-cyan-300", count: "text-cyan-400" },
  this_week: { dot: "bg-blue-400", text: "text-blue-300", count: "text-blue-400" },
  this_month: { dot: "bg-slate-400", text: "text-slate-300", count: "text-slate-400" },
  older: { dot: "bg-slate-600", text: "text-slate-500", count: "text-slate-600" },
};

function _startOfDay(d) {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

function _classifyByDate(note, now = new Date()) {
  const raw = note.created_at;
  if (!raw) return "older";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "older";

  const today0 = _startOfDay(now);
  const tomorrow0 = new Date(today0);
  tomorrow0.setDate(tomorrow0.getDate() + 1);
  const week0 = new Date(today0);
  week0.setDate(week0.getDate() - 6); // últimos 7 dias = today + 6
  const month0 = new Date(today0);
  month0.setDate(month0.getDate() - 30);

  if (d >= today0 && d < tomorrow0) return "today";
  if (d >= week0 && d < today0) return "this_week";
  if (d >= month0 && d < week0) return "this_month";
  return "older";
}

// ===================== TYPE GROUPING =====================

// Ordem dos grupos no modo "type": pinned no topo, depois ideias e
// pesquisas (mais frequentes), revisões no fim (geradas automáticas).
export const TYPE_GROUP_ORDER = [
  "pinned", "idea", "research", "journal", "vent", "daily_review", "general",
];

export const TYPE_GROUP_LABELS = {
  pinned: "Fixadas",
  ...Object.fromEntries(
    Object.entries(NOTE_TYPES).map(([k, v]) => [k, v.plural || v.label]),
  ),
};

export const TYPE_GROUP_COLORS = Object.fromEntries(
  Object.entries(NOTE_TYPES).map(([k, v]) => [
    k,
    {
      dot: "",  // usa style inline com v.color
      text: "text-slate-300",
      count: "text-slate-400",
      hex: v.color,
    },
  ]),
);

// ===================== PUBLIC GROUPING =====================

/**
 * Agrupa notes em buckets temporais ou por tipo.
 *
 * mode: "date" | "type"
 * Retorna { [groupKey]: notes[] } ordenado por created_at desc dentro.
 * Tipos desconhecidos caem em "general".
 */
export function groupNotes(notes, mode = "date", now = new Date()) {
  const out = {};
  const order = mode === "type" ? TYPE_GROUP_ORDER : DATE_GROUP_ORDER;
  for (const k of order) out[k] = [];

  for (const n of notes || []) {
    // PRODUTO-V1 N6 — pinned vence qualquer outro bucket (não duplica).
    if (n && n.pinned === true) {
      out.pinned.push(n);
      continue;
    }
    let key;
    if (mode === "type") {
      key = NOTE_TYPES[n.type] ? n.type : "general";
    } else {
      key = _classifyByDate(n, now);
    }
    if (!out[key]) out[key] = [];
    out[key].push(n);
  }
  // Sort cada bucket por created_at desc.
  for (const k of order) {
    out[k].sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });
  }
  return out;
}

// ===================== HERO PICK =====================

/**
 * Escolhe 1 nota pra renderizar no Hero card.
 *   1. Se há nota com pinned=true, retorna o pinned mais recente.
 *   2. Senão, retorna a nota com created_at mais recente.
 *   3. Lista vazia → null.
 */
export function pickHeroNote(notes) {
  if (!Array.isArray(notes) || notes.length === 0) return null;
  const pinned = notes.filter((n) => n.pinned === true);
  if (pinned.length > 0) {
    return [...pinned].sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    })[0];
  }
  return [...notes].sort((a, b) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  })[0];
}

// ===================== TIME LABEL =====================

export function formatNoteTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return "agora";
  if (diffMin < 60) return `há ${diffMin}min`;
  if (diffH < 24) return `há ${diffH}h`;
  if (diffD === 1) return "ontem";
  if (diffD < 7) return `há ${diffD} dias`;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}
